<template>
    <div style="width: 100%; flex: 1 1 auto">
        <ag-grid-vue
            class="ag-theme-alpine grid"
            :column-defs="columnDefs"
            :default-col-def="defaultColDef"
            :row-data="rowData"
            :on-grid-ready="onGridReady"
            :on-body-scroll="onBodyScroll"
            :dom-layout="domLayout"
            :pagination="true"
            :pagination-page-size="10"
            :no-rows-overlay-component="NoRowsOverlay"
            :no-rows-overlay-component-params="noRowsOverlayParams"
            :loading-overlay-component="SpinningLoader"
            :default-excel-export-params="defaultExcelExportParams"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, defineProps } from 'vue';
import { AgGridVue } from 'ag-grid-vue3';
import {
    GridApi, GridReadyEvent, ColumnApi
} from 'ag-grid-community';

import { InteractionSummaryTypes, BaseModel } from '../../types';
import { useWindowSize } from '../../composables/useWindowSize';
import { WorkstreamInteractionGrouping, WorkstreamInteractionWorkstreamData } from '../interactionSummary/getData';
import numberValue from '../../utils/numberValue';

import NoRowsOverlay from '../common/noRowsOverlay/NoRowsOverlay.vue';
import SpinningLoader from '../common/loader/SpinningLoader.vue';
import { ValueFormatterParams } from 'ag-grid-community';

interface Props {
  data: WorkstreamInteractionWorkstreamData;
  workstreams: BaseModel[];
  workloads: BaseModel[];
}

const props = defineProps<Props>();
const columnDefs = ref<InteractionSummaryTypes.ColumnDefsType[] | []>([]);
const defaultColDef = ref<InteractionSummaryTypes.DefaultColumnDefsType>({
    resizable: true,
    filter: true,
    flex: 1,
    sortable: true,
    minWidth: 100,
    cellStyle: { 'text-align:': 'left' }
});

const defaultExcelExportParams = ref<InteractionSummaryTypes.DefaultExcelExportParamsType>({
    fileName: 'workstream_interactions_by_types_export',
    sheetName: 'workstream_interactions_by_types'
});

const rowData = ref<
  InteractionSummaryTypes.WorkstreamInteractionByTypeRow[] | null
>(null);
const gridApi = ref<GridApi | null>(null);
const gridColumnApi = ref<ColumnApi | null>(null);
const domLayout = 'autoHeight';
let noRowsOverlayMessage = 'Please apply a filter';
const noRowsOverlayParams = { noRowsMessageFunc: () => noRowsOverlayMessage };

const autoSizeAll = (skipHeader: boolean) => {
    if (gridColumnApi?.value) {
        const allColumnIds: string[] = [];
        gridColumnApi.value.getAllColumns()?.forEach((column) => {
            if (column) allColumnIds.push(column.getId());
        });

        gridColumnApi.value.autoSizeColumns(allColumnIds, skipHeader);
    }
};
const onBodyScroll = () => {
    autoSizeAll(false);
};
const handleResize = () => {
    autoSizeAll(false);
};
const onGridReady = async (evt: GridReadyEvent) => {
    gridApi.value = evt.api;
    gridColumnApi.value = evt.columnApi;
    gridApi.value?.showNoRowsOverlay();

    await viewData(props.data);

    autoSizeAll(false);
};

useWindowSize(handleResize);

const formatValue = (decimalNum: number | string) => {
    if (decimalNum === '' || decimalNum === 0 || !decimalNum) {
        return 0;
    }
    const number = Number(decimalNum);
    return numberValue(number);
};

const getWorkstreamName = (nKey: number) => {
    let workstreamName = 'N/A';
    if (!Number.isNaN(nKey)) {
        const foundWorkstream = props.workstreams.find((workstream) => workstream.id === nKey);
        if (foundWorkstream) {
            workstreamName = foundWorkstream.name;
        }
    }
    return workstreamName;
};
const getWorkloadName = (groupName: number | string) => {
    let workloadName = 'N/A';
    if (typeof groupName !== 'string') {
        const nKey = Number.parseInt(groupName + '');
        if (!Number.isNaN(nKey)) {
            const foundWorkload = props.workloads.find((workload) => workload.id === nKey);
            // eslint-disable-next-line max-depth
            if (foundWorkload) {
                workloadName = foundWorkload.name;
            }
        }
    } else {
        workloadName = groupName;
    }
    return workloadName;
};
const sortGroupings = (groupingTypes: WorkstreamInteractionGrouping[]) => {
    let sortedGroupTypes = groupingTypes || [];
    if (sortedGroupTypes.length && typeof sortedGroupTypes[0].groupName !== 'string') {
    // N/A is last
        sortedGroupTypes = sortedGroupTypes.sort((a, b) => Number.parseInt(b.groupName + '') - Number.parseInt(a.groupName + ''));
    }
    return sortedGroupTypes;
};

const viewData = async (data: WorkstreamInteractionWorkstreamData) => {
    gridApi?.value?.showLoadingOverlay();

    const initialColumn: InteractionSummaryTypes.ColumnDefsType = {
        headerName: '',
        children: [
            {
                headerName: 'Workstream Name',
                field: 'workstreamName'
            },
            {
                headerName: 'Total Interactions',
                field: 'totalInteractions',
                valueFormatter: (params) => formatValue(params.value)
            },
            {
                headerName: 'Total Handle Time',
                field: 'totalHandleTime',
                valueFormatter: (params) => formatValue(params.value)
            },
            {
                headerName: 'Avg Handle Time',
                field: 'averageHandleTime',
                valueFormatter: (params) => formatValue(params.value)
            }
        ]
    };
    const allColumns = [initialColumn];
    for (const [ key, value ] of Object.entries(data)) {
        const nKey = Number.parseInt(key + '');
        const workloadGroupings = sortGroupings(value.grouping);
        value.workstreamName = getWorkstreamName(nKey);
        workloadGroupings.forEach((group: WorkstreamInteractionGrouping) => {
            const groupKey = getWorkloadName(group.groupName);
            if (groupKey === 'N/A' && group.totalInteractions === 0 && group.averageHandleTime === 0 && group.totalHandleTime === 0) {
                return;
            }
            const foundColumn = allColumns.find((column) => column.headerName === groupKey);
            if (!foundColumn) {
                const nextColumn = {
                    headerName: groupKey,
                    children: [
                        {
                            headerName: 'Total Interactions',
                            field: `${groupKey}-totalInteractions`,
                            valueFormatter: (params: ValueFormatterParams) => formatValue(params.value)
                        },
                        {
                            headerName: 'Total Handle Time',
                            field: `${groupKey}-totalHandleTime`,
                            valueFormatter: (params: ValueFormatterParams) => formatValue(params.value)
                        },
                        {
                            headerName: 'Avg Handle Time',
                            field: `${groupKey}-averageHandleTime`,
                            valueFormatter: (params: ValueFormatterParams) => formatValue(params.value)
                        }
                    ]
                };
                allColumns.push(nextColumn);
            }
            // @ts-expect-error - Need to fix but don't understand enough. This presumably works at runtime.
            data[nKey][`${groupKey}-averageHandleTime`] = group.averageHandleTime;
            // @ts-expect-error - Need to fix but don't understand enough. This presumably works at runtime.
            data[nKey][`${groupKey}-totalHandleTime`] = group.totalHandleTime;
            // @ts-expect-error - Need to fix but don't understand enough. This presumably works at runtime.
            data[nKey][`${groupKey}-totalInteractions`] = group.totalInteractions;
        });
    }
    columnDefs.value = allColumns;
    noRowsOverlayMessage = 'No rows to show';
    // @ts-expect-error - Need to fix but don't understand enough. This presumably works at runtime.
    rowData.value = Object.entries(data)
    // Sort data, N/A last
        .sort((a, b) => Number.parseInt(b[0]) - Number.parseInt(a[0]))
        .map(([ _key, value ]) => value);
    setTimeout(() => {
        gridApi.value?.hideOverlay();
    }, 5000);
};
</script>

<style scoped lang="scss" src="./style.scss"></style>
